import { FC, useCallback, useEffect, useRef, useState } from 'react'

import { TagPicker } from './InteractiveTagBlock'
import { Tags, useUpdateAResourceTags } from '@api/orval/apiHooks'
import { Any } from '@api/orvalFetcher'

interface TagsCellProps {
  resourceId?: number
  initialTags?: string[] | null
  resourceType: 'users' | 'trading_accounts' | 'payouts'
  tagList?: Tags | null
}

export const TagsCell: FC<TagsCellProps> = ({ resourceId, resourceType, initialTags, tagList }) => {
  // Default params only guard `undefined`; the backend can send `null` for either list.
  const safeInitialTags = initialTags ?? []
  const safeTagList = tagList ?? []

  const [selected, setSelected] = useState<string[]>(safeInitialTags)
  const [knownTags, setKnownTags] = useState<string[]>(safeTagList)
  const previousRef = useRef<string[]>(safeInitialTags)

  useEffect(() => {
    setSelected(initialTags ?? [])
    previousRef.current = initialTags ?? []
  }, [initialTags])

  useEffect(() => {
    setKnownTags(tagList ?? [])
  }, [tagList])

  const updateTagsMutation = useUpdateAResourceTags({
    mutation: {
      onMutate: (variables) => {
        const snapshot = previousRef.current
        previousRef.current = variables.data.tag_list ?? []

        return { snapshot }
      },
      onError: (_err, _variables, context) => {
        const ctx = context as { snapshot: string[] } | undefined
        if (ctx?.snapshot) {
          setSelected(ctx.snapshot)
          previousRef.current = ctx.snapshot
        }
      },
      onSuccess: (response) => {
        const data = response?.data as Any
        if (data?.tag_list) {
          const next: string[] = data.tag_list
          setSelected(next)
          previousRef.current = next
          setKnownTags((prev) => {
            const seen = new Set(prev.map((t) => t.toLowerCase()))
            const merged = [...prev]
            for (const t of next) {
              if (!seen.has(t.toLowerCase())) {
                merged.push(t)
                seen.add(t.toLowerCase())
              }
            }

            return merged
          })
        }
      },
    },
  })

  const handleChange = useCallback(
    (next: string[]) => {
      if (!resourceId) return
      setSelected(next)
      updateTagsMutation.mutate({
        resourceType,
        resourceId,
        data: { tag_list: next },
      })
    },
    [resourceId, resourceType, updateTagsMutation],
  )

  return (
    <TagPicker
      selected={selected}
      allTags={knownTags}
      onChange={handleChange}
      isLoading={updateTagsMutation.isPending}
      disabled={!resourceId}
    />
  )
}
